import React, { useMemo } from "react";
import {
  Stack,
  Heading,
  Text,
  Box,
  Button,
  TextLink,
  Alert,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { EICFormData } from "./types";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { getIsElectricityAndGas, getPostalAddress } from "./helper";
import { useFormValidation } from "./useFormValidation";

import LifeSupportFields from "./LifeSupportFields";
import ConcessionBlock from "./ConcessionBlock";
import { State } from "src/components/Premises";

interface EICFormPageThreeReadOnlyProps {
  onBack: () => void;
  onContinue: () => void;
}

const EICFormPageThreeReadOnly: React.FC<EICFormPageThreeReadOnlyProps> = ({
  onBack,
  onContinue,
}) => {
  const { values, setFieldValue, errors } = useFormikContext<EICFormData>();

  const billDeliveryMethodLabel =
    values.billDeliveryMethod === "post" ? "Post" : "Email";

  const hasElectricityAndGas = useMemo(
    () => getIsElectricityAndGas(values.connectionTypes),
    [values.connectionTypes]
  );

  const { isValidating, handleContinueClick } = useFormValidation(onContinue);

  return (
    <Stack
      space={["xlarge", "xxlarge"]}
      data-role="section"
      id="eic-form-page-three-read-only"
    >
      {/* Content */}
      <Stack>
        <Heading variant="h3">Additional details</Heading>
        <Text>
          We just need a few more details from you to setup your new account.
        </Text>
      </Stack>
      {/* Content */}

      {/* Bills & Letters */}
      <Stack space={["large", "xlarge"]}>
        <Heading variant="h4">Your bills and letters</Heading>
        <Stack>
          {/* Comms preference */}
          <ReadOnlyTextField
            label="Where would you like your bills and information sent?"
            value={billDeliveryMethodLabel}
            helperText={
              values.billDeliveryMethod === "post" ? (
                <Text variant="body-small" tone="neutralLight">
                  A paper fee of $1.75 per bill incl. GST{" "}
                  <TextLink
                    href="https://www.originenergy.com.au/pricing/additional-charges/paper-bill-fee/"
                    target="_blank"
                  >
                    may apply
                  </TextLink>{" "}
                  if you choose bills by post.
                </Text>
              ) : null
            }
          />
          <Alert>
            <Text>
              Even with {billDeliveryMethodLabel} selected, we may sometimes
              contact you by {billDeliveryMethodLabel} or SMS. If this isn't
              safe for you, call us on{" "}
              <TextLink href="tel:1300 137 932">1300 137 932</TextLink> before
              continuing.
            </Text>
          </Alert>
          {/* End Comms preference */}
          {/* Postal address */}
          <ReadOnlyTextField
            label="Postal address"
            value={getPostalAddress(values)}
          />
          {/* End Postal address */}
        </Stack>
      </Stack>
      {/* End Bills & Letters */}

      {/* Concession  */}
      <ConcessionBlock state={values.state} readOnly />
      {/* End of Concession */}

      {values.state === State.VIC && (
        <LifeSupportFields
          values={values}
          errors={errors}
          setFieldValue={setFieldValue}
          hasElectricityAndGas={hasElectricityAndGas}
        />
      )}
      {/* End Primary contact */}
      {/* Actions  */}
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row-reverse" }}
      >
        <Button onClick={handleContinueClick} spinner={isValidating}>
          Continue
        </Button>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Box>
      {/* End Actions */}
    </Stack>
  );
};

export default EICFormPageThreeReadOnly;
